/**
 * The run viewer — SPEC_5 Decision 6: the old wake view, made one line per step (2026-10-22 redesign).
 *
 * A live run (from the stream) and a finished one (from `GET /runs/{id}/events`) differ only in their
 * source, because **a past run must replay exactly as it streamed**. Two renderers would drift, and the
 * drift would land on the one screen whose job is to answer *"why did it decide that"*.
 *
 * Every step — the briefing, the thinking, every tool call — is one collapsed line with a plain name a
 * teammate can read ("Submitted the plan", not `submit_plan`), the first line of what came back, and
 * how long it took; a click opens the full input and output. A plan attempt carries ✓ or ✗. What stays
 * open is what must be read: a refusal, an error, and the AI's final reply.
 *
 * Every disclosure is a real `<button aria-expanded>`, and what it opens is `[hidden]` rather than a height
 * animation — **animating a height is animating layout**, and a streaming run must grow downward without
 * moving what is above it.
 */

import { useState } from "react";
import type { RunEvent } from "../../api/types/competition";
import { useT } from "../../i18n/useT";
import type { Keys } from "../../i18n/en";
import { clock, span } from "../../lib/time";
import { Markdown } from "../ui/Markdown";

/** Each of the agent's tools (`graph/tools/__init__.py`'s TOOLS) by a name a teammate can read. A tool
 *  not listed here shows its own name, so a new tool is never hidden. */
const PLAIN: Record<string, Keys> = {
  recall: "log.recall",
  remember: "log.remember",
  note_to_next_run: "log.note_to_next_run",
  web_search: "log.web_search",
  view: "log.view",
  create: "log.create",
  str_replace: "log.edit",
  insert: "log.edit",
  run_command: "log.run_command",
  ask_analyst: "log.ask_analyst",
  read_datapacks: "log.read_datapacks",
  read_dplans: "log.read_dplans",
  use_skill: "log.use_skill",
  submit_plan: "log.submit_plan",
};

export interface WakeSource {
  wakeId: string;
  at: string;
  occasion: string[] | null;
  /** The snapshot the wake opened with. */
  preamble: string;
  thinking: string;
  text: string;
  events: RunEvent[];
  reply: { content: string; cost_usd: number | null } | null;
  running: boolean;
  endedAt?: string | null;
}

type Block =
  | { kind: "snapshot"; text: string }
  | { kind: "thinking"; text: string }
  | { kind: "call"; id: string; name: string; args: unknown; result: string | null; ms: number | null; failed: boolean; pending: boolean; at: string }
  | { kind: "refusal"; rule: string; sentence: string; at: string }
  | { kind: "reply"; text: string }
  | { kind: "resting"; until: string | null; why: string | null }
  | { kind: "error"; message: string; where: string | null };

const str = (data: Record<string, unknown>, key: string): string | null =>
  typeof data[key] === "string" ? (data[key] as string) : null;
const numOf = (data: Record<string, unknown>, key: string): number | null =>
  typeof data[key] === "number" ? (data[key] as number) : null;

/**
 * The transcript, in the order it happened. Built from the event list rather than from a summary, because
 * a tool call that has started and not ended must show as exactly that — with its elapsed time and
 * nothing else moving.
 */
export function blocksOf(source: WakeSource): Block[] {
  const blocks: Block[] = [];
  if (source.preamble) blocks.push({ kind: "snapshot", text: source.preamble });

  const calls = new Map<string, number>(); // tool id → index in `blocks`

  for (const event of source.events) {
    const data = event.data ?? {};
    switch (event.type) {
      case "tool_start": {
        const id = str(data, "id") ?? `${event.seq}`;
        const name = str(data, "name") ?? "tool";
        blocks.push({
          kind: "call",
          id,
          name,
          args: data.args,
          result: null,
          ms: null,
          failed: false,
          pending: true,
          at: event.at,
        });
        calls.set(id, blocks.length - 1);
        break;
      }
      case "tool_end": {
        const id = str(data, "id") ?? "";
        const where = calls.get(id);
        if (where === undefined) break;
        const was = blocks[where] as Extract<Block, { kind: "call" }>;
        blocks[where] = {
          ...was,
          result: str(data, "result"),
          ms: numOf(data, "duration_ms"),
          failed: data.is_error === true,
          pending: false,
        };
        break;
      }
      case "refusal":
        blocks.push({
          kind: "refusal",
          rule: str(data, "rule") ?? "the fence",
          sentence: str(data, "sentence") ?? "",
          at: event.at,
        });
        break;
      case "resting":
        blocks.push({ kind: "resting", until: str(data, "until"), why: str(data, "why") });
        break;
      case "error":
        blocks.push({ kind: "error", message: str(data, "message") ?? "", where: str(data, "where") });
        break;
      default:
        break;
    }
  }

  // Thinking is one collapsed line however many deltas it arrived in, and it sits where the first one
  // did — before the first call. Putting it at the end would tell the wrong story about the order.
  if (source.thinking) {
    const first = blocks.findIndex((b) => b.kind === "call");
    const block: Block = { kind: "thinking", text: source.thinking };
    if (first === -1) blocks.push(block);
    else blocks.splice(first, 0, block);
  }

  const said = source.text || source.reply?.content || "";
  if (said) blocks.push({ kind: "reply", text: said });

  return blocks;
}

/** One collapsed line, with the disclosure it opens. */
function Collapsed({
  kind,
  tail,
  mark,
  bad,
  tool,
  sign,
  children,
}: {
  kind: string;
  tail?: string;
  mark?: string;
  bad?: boolean;
  /** The tool's own name, for the e2e and for whoever opens the line. */
  tool?: string;
  /** ✓ or ✗ on a plan attempt. */
  sign?: "ok" | "bad";
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="d3line" aria-expanded={open} onClick={() => setOpen(!open)} data-tool={tool}>
        <svg className="d3line__v" viewBox="0 0 10 10" aria-hidden="true">
          <path d="M3 1.5 7.5 5 3 8.5Z" fill="currentColor" />
        </svg>
        {sign ? (
          <span className="d3line__sign" data-sign={sign} aria-hidden="true">
            {sign === "ok" ? "✓" : "✗"}
          </span>
        ) : null}
        <span className="d3line__kind">{kind}</span>
        {tail ? <span className="d3line__tail">{tail}</span> : null}
        {mark ? <span className={`d3line__mark${bad ? " d3line__mark--bad" : ""}`}>{mark}</span> : null}
      </button>
      <div className="d3out" hidden={!open}>
        {children}
      </div>
    </>
  );
}

export function RunView({ source }: { source: WakeSource }) {
  const { t } = useT();
  const blocks = blocksOf(source);

  return (
    <div className="run" data-wake={source.wakeId} data-running={source.running ? "" : undefined}>
      {source.running ? <span className="wake-mark">{t("d2.running")}</span> : null}

      {blocks.map((block, i) => {
        switch (block.kind) {
          case "snapshot":
            return (
              <Collapsed key={`snap-${i}`} kind={t("log.snapshot")}>
                <pre className="d3pre">{block.text}</pre>
              </Collapsed>
            );

          case "thinking":
            // A live wake's collapsed thinking line shows the LATEST streamed line, so the line ticks
            // while nothing around it moves.
            return (
              <Collapsed
                key={`think-${i}`}
                kind={t("log.thinking")}
                tail={lastLine(block.text)}
              >
                <pre className="d3pre">{block.text}</pre>
              </Collapsed>
            );

          case "call": {
            const elapsed = block.pending ? t("d3.elapsed") : span(block.ms === null ? null : block.ms / 1000);
            const plain = PLAIN[block.name];
            const args = (block.args ?? {}) as Record<string, unknown>;
            const attempt = block.name === "submit_plan" && !block.pending;
            const tail =
              block.name === "ask_analyst" && block.pending
                ? `${t("d3.analystWorking")} · ${t("d3.expected")}`
                : block.name === "use_skill" && typeof args.name === "string"
                  ? args.name
                  : (block.result ?? "").split("\n")[0];
            return (
              <Collapsed
                key={`call-${block.id}`}
                kind={plain ? t(plain) : block.name}
                tool={block.name}
                tail={tail}
                mark={elapsed}
                bad={block.failed}
                sign={attempt ? ((block.result ?? "").startsWith("READY") ? "ok" : "bad") : undefined}
              >
                <p className="d3tool mono">
                  {block.name} · {clock(block.at)}
                </p>
                <pre className="d3pre">{JSON.stringify(block.args, null, 1)}</pre>
                {block.result ? <pre className="d3pre">{block.result}</pre> : null}
              </Collapsed>
            );
          }

          case "refusal":
            // A fence verdict: open, always, and quoted because it is a quotation.
            return (
              <div key={`refusal-${i}`} className="act act--refused" data-state="refused" data-rule={block.rule}>
                <p className="act__head">
                  <span>{t("d3.refused")}</span>
                  <span className="act__tool">{block.rule}</span>
                  <span className="act__tool">{clock(block.at)}</span>
                </p>
                <blockquote className="verbatim">
                  {block.sentence}
                  <cite>{block.rule}</cite>
                </blockquote>
              </div>
            );

          case "reply":
            // The one block that gets a little room, and the only one rendered as markdown.
            return (
              <div key={`reply-${i}`} className="reply" data-block="reply">
                <p className="reply__n">{t("d3.reply")}</p>
                <Markdown text={block.text} className="reply__body" />
              </div>
            );

          case "resting":
            // The last thing, because it is the only part still true.
            return (
              <p key={`rest-${i}`} className="restline" data-state="rested">
                <span className="lbl">{t("d3.rested")}</span>
                {block.until ? <b>{clock(block.until)}</b> : null}
                {block.why ? <span>{block.why}</span> : null}
              </p>
            );

          case "error":
            return (
              <p key={`err-${i}`} className="errend" data-state="ended in error">
                <b>{t("d3.error")}</b>
                <span>{block.message}</span>
                {block.where ? <span className="sub">{block.where}</span> : null}
              </p>
            );

          default:
            return null;
        }
      })}
    </div>
  );
}

function lastLine(text: string): string {
  const lines = text.split("\n").filter((line) => line.trim() !== "");
  return lines.length ? lines[lines.length - 1] : "";
}
